'use client';

import { useCallback, useEffect, useState } from 'react';
import { BusinessEntityFormData } from '../types';
import { useBusinessEntityFields } from './useBusinessEntityFields';

/**
 * Props for useBusinessEntityCreation hook
 */
interface UseBusinessEntityCreationProps {
  /** Parent field ID for namespacing form data */
  parentFieldId: string;
  /** Callback when entity data changes */
  onEntityInfoChange?: (entityId: string, data: BusinessEntityFormData) => void;
}

/**
 * Return type for useBusinessEntityCreation hook
 */
interface UseBusinessEntityCreationResult {
  // Cascading dropdown data
  cascading: ReturnType<typeof useBusinessEntityFields>;

  // Entity form data management
  getEntityFormData: (entityId: string) => BusinessEntityFormData;
  updateEntityFormData: (
    entityId: string,
    data: Partial<BusinessEntityFormData>
  ) => void;

  // Validation
  validateEntityFormData: (entityId: string) => Record<string, string>;
  isEntityInfoFilled: (entityId: string) => boolean;

  // Get display name for an entity
  getEntityDisplayName: (entityId: string) => string;
}

/**
 * Default empty form data for a new business entity
 */
const getDefaultBusinessEntityFormData = (): BusinessEntityFormData => ({
  legalName: '',
  incorporationCountry: null,
  jurisdiction: null,
  businessEntityType: null,
});

/**
 * Hook for managing business entity creation state
 *
 * This hook handles:
 * - Form data state for multiple new entities
 * - Cascading dropdown logic (auto-select when single option)
 * - Validation of required fields
 * - Display name generation
 *
 * @example
 * ```tsx
 * const {
 *   cascading,
 *   getEntityFormData,
 *   updateEntityFormData,
 *   validateEntityFormData,
 *   isEntityInfoFilled,
 * } = useBusinessEntityCreation({ parentFieldId: 'my-field' });
 *
 * // Get form data for a specific entity
 * const formData = getEntityFormData('new-123');
 *
 * // Update form data
 * updateEntityFormData('new-123', { legalName: 'Acme Corp' });
 *
 * // Check if entity is complete
 * const isComplete = isEntityInfoFilled('new-123');
 * ```
 */
export function useBusinessEntityCreation({
  parentFieldId,
  onEntityInfoChange,
}: UseBusinessEntityCreationProps): UseBusinessEntityCreationResult {
  // Cascading dropdown logic
  const cascading = useBusinessEntityFields();

  // Store form data for each new entity
  const [entityFormDataMap, setEntityFormDataMap] = useState<
    Record<string, BusinessEntityFormData>
  >({});

  // Get form data for an entity (creates default if doesn't exist)
  const getEntityFormData = useCallback(
    (entityId: string): BusinessEntityFormData => {
      return entityFormDataMap[entityId] || getDefaultBusinessEntityFormData();
    },
    [entityFormDataMap]
  );

  // Update form data for an entity with cascading logic
  const updateEntityFormData = useCallback(
    (entityId: string, data: Partial<BusinessEntityFormData>) => {
      setEntityFormDataMap((prev) => {
        const currentData =
          prev[entityId] || getDefaultBusinessEntityFormData();
        const newData = { ...currentData, ...data };

        // Handle cascading logic when country changes
        if (data.incorporationCountry !== undefined) {
          const country = data.incorporationCountry;

          if (country !== currentData.incorporationCountry) {
            // Reset dependent fields when country changes
            newData.jurisdiction = null;
            newData.businessEntityType = null;
            newData.entityLegalFormId = null;

            // Auto-select jurisdiction if only one option
            if (country) {
              const autoJurisdiction =
                cascading.getAutoSelectJurisdiction(country);
              if (autoJurisdiction) {
                newData.jurisdiction = autoJurisdiction;

                // Also try to auto-select entity type
                const autoLegalFormId =
                  cascading.getAutoSelectEntityLegalFormId(
                    country,
                    autoJurisdiction
                  );
                if (autoLegalFormId) {
                  newData.entityLegalFormId = autoLegalFormId;
                  newData.businessEntityType =
                    cascading.getCorporationTypeForLegalForm(
                      country,
                      autoJurisdiction,
                      autoLegalFormId
                    );
                }
              }
            }
          }
        }

        // Handle cascading logic when jurisdiction changes
        if (
          data.jurisdiction !== undefined &&
          data.incorporationCountry === undefined
        ) {
          const { jurisdiction } = data;

          if (jurisdiction !== currentData.jurisdiction) {
            // Reset entity type when jurisdiction changes
            newData.businessEntityType = null;
            newData.entityLegalFormId = null;

            // Auto-select entity type if only one option
            if (jurisdiction && newData.incorporationCountry) {
              const autoLegalFormId =
                cascading.getAutoSelectEntityLegalFormId(
                  newData.incorporationCountry,
                  jurisdiction
                );
              if (autoLegalFormId) {
                newData.entityLegalFormId = autoLegalFormId;
                newData.businessEntityType =
                  cascading.getCorporationTypeForLegalForm(
                    newData.incorporationCountry,
                    jurisdiction,
                    autoLegalFormId
                  );
              }
            }
          }
        }

        return {
          ...prev,
          [entityId]: newData,
        };
      });
    },
    [cascading]
  );

  // Notify parent when entity data changes
  useEffect(() => {
    if (onEntityInfoChange) {
      Object.entries(entityFormDataMap).forEach(([entityId, data]) => {
        onEntityInfoChange(entityId, data);
      });
    }
  }, [entityFormDataMap, onEntityInfoChange]);

  // Validate form data for an entity
  const validateEntityFormData = useCallback(
    (entityId: string): Record<string, string> => {
      const data = getEntityFormData(entityId);
      const errors: Record<string, string> = {};

      if (!data.legalName || data.legalName.trim() === '') {
        errors.legalName = 'Legal name is required';
      }

      if (!data.incorporationCountry) {
        errors.incorporationCountry = 'Country is required';
      }

      if (!data.jurisdiction) {
        errors.jurisdiction = 'Jurisdiction is required';
      }

      if (!data.entityLegalFormId) {
        errors.businessEntityType = 'Entity type is required';
      }

      return errors;
    },
    [getEntityFormData]
  );

  // Check if an entity has all required fields filled
  const isEntityInfoFilled = useCallback(
    (entityId: string): boolean => {
      const errors = validateEntityFormData(entityId);
      return Object.keys(errors).length === 0;
    },
    [validateEntityFormData]
  );

  // Get display name for an entity
  const getEntityDisplayName = useCallback(
    (entityId: string): string => {
      const data = getEntityFormData(entityId);

      if (data.legalName && data.legalName.trim() !== '') {
        return data.legalName;
      }

      return 'New Business Entity';
    },
    [getEntityFormData]
  );

  return {
    cascading,
    getEntityFormData,
    updateEntityFormData,
    validateEntityFormData,
    isEntityInfoFilled,
    getEntityDisplayName,
  };
}
